/**
 * Access-token store.
 *
 * The backend authenticates via httpOnly cookies *and* accepts a Bearer token
 * (see auth.middleware.ts). Cookies cover same-site dev; the Bearer token makes
 * the client robust for cross-site production deployments where the SPA and API
 * live on different registrable domains. The short-lived access token is kept in
 * memory (primary) with a localStorage mirror so a page refresh stays signed in
 * until the next silent refresh. The long-lived refresh token never touches JS —
 * it remains an httpOnly cookie managed entirely by the server.
 */

const STORAGE_KEY = "fmis.accessToken";

let accessToken: string | null = readInitial();

function readInitial(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export const tokenStore = {
  get(): string | null {
    return accessToken;
  },
  set(token: string | null): void {
    accessToken = token;
    try {
      if (token) localStorage.setItem(STORAGE_KEY, token);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable (private mode / SSR) — in-memory only */
    }
    listeners.forEach((fn) => fn(token));
  },
  clear(): void {
    this.set(null);
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
